@if (operations().length) {
  <section
    #overlayEl
    class="wallet-task-overlay"
    [class.collapsed]="isCollapsed()"
    popover="manual"
    aria-live="polite"
    cdkDrag
    cdkDragBoundary="body"
    [cdkDragFreeDragPosition]="dragPosition()"
    (cdkDragEnded)="onDragEnded($event)"
  >
    <div class="overlay-head">
      <header cdkDragHandle>
        <div class="header-title">
          <span>Wallet operations</span>
          @if (isCollapsed()) {
            <div class="mini-badges" [attr.aria-label]="summaryText()">
              <span
                class="mini-badge is-active"
                [class.has-value]="activeCount() > 0"
                aria-hidden="true"
              >
                <kc-icon
                  [iconClass]="activeCount() > 0 ? 'loading-02' : 'info-circle'"
                  size="xs"
                />
                {{ activeCount() }}
              </span>
              <span
                class="mini-badge is-success"
                [class.has-value]="completedCount() > 0"
                aria-hidden="true"
              >
                <kc-icon iconClass="check-circle" size="xs" />
                {{ completedCount() }}
              </span>
              <span
                class="mini-badge is-danger"
                [class.has-value]="attentionCount() > 0"
                aria-hidden="true"
              >
                <kc-icon iconClass="alert-triangle" size="xs" />
                {{ attentionCount() }}
              </span>
            </div>
          } @else {
            <small>{{ summaryText() }}</small>
          }
        </div>
        <div class="header-actions">
          <!-- Cards here are transient (dismissible, gone on reload); the
               durable record lives on the Portfolio Activity tab (#251). -->
          <kc-button
            class="history-link-button"
            text="View history"
            size="s"
            variant="link-secondary"
            (buttonClick)="openHistory()"
          />
          @if (canClearAll()) {
            <kc-button
              class="clear-all-button"
              text="Clear all"
              size="s"
              variant="link-secondary"
              (buttonClick)="requestClearAll()"
            />
          }
          <kc-icon-button
            class="collapse-toggle"
            [class.is-collapsed]="isCollapsed()"
            icon="chevron-down"
            size="sm"
            variant="secondary"
            [ariaLabel]="
              isCollapsed()
                ? 'Expand wallet operations'
                : 'Collapse wallet operations'
            "
            (buttonClick)="toggleCollapsed()"
          />
        </div>
      </header>

      @if (confirmingClearAll()) {
        <div class="stop-tracking-confirm">
          <p>
            Clear everything? This stops tracking
            {{ submittedCount() }} pending operation{{
              submittedCount() === 1 ? '' : 's'
            }}
            —
            {{
              submittedCount() === 1 ? 'its token queue' : 'their token queues'
            }}
            will continue, but the transaction{{
              submittedCount() === 1 ? '' : 's'
            }}
            may still complete on-chain, so verify
            {{ submittedCount() === 1 ? 'it' : 'them' }} on the explorer first.
            This does not cancel the transaction{{
              submittedCount() === 1 ? '' : 's'
            }}.
          </p>
          <div class="confirm-actions">
            <kc-button
              class="confirm-clear-button"
              text="Clear everything"
              size="s"
              variant="secondary"
              (buttonClick)="confirmClearAll()"
            />
            <kc-button
              class="keep-waiting-button"
              text="Keep waiting"
              size="s"
              variant="primary"
              (buttonClick)="cancelClearAll()"
            />
          </div>
        </div>
      }
    </div>

    <div
      class="expandable-body"
      [class.is-collapsed]="isCollapsed()"
      [attr.inert]="isCollapsed() ? '' : null"
    >
      <div class="expandable-body-inner">
        <div class="task-summary-grid">
          <span class="task-summary-pill" [class.is-active]="activeCount() > 0">
            <kc-icon
              class="pill-icon"
              [iconClass]="activeCount() > 0 ? 'loading-02' : 'info-circle'"
              size="xs"
              aria-hidden="true"
            />
            <strong>{{ activeCount() }}</strong>
            <span>Active</span>
          </span>
          <span
            class="task-summary-pill"
            [class.is-success]="completedCount() > 0"
          >
            <kc-icon
              class="pill-icon"
              iconClass="check-circle"
              size="xs"
              aria-hidden="true"
            />
            <strong>{{ completedCount() }}</strong>
            <span>Done</span>
          </span>
          <span
            class="task-summary-pill"
            [class.is-danger]="attentionCount() > 0"
          >
            <kc-icon
              class="pill-icon"
              iconClass="alert-triangle"
              size="xs"
              aria-hidden="true"
            />
            <strong>{{ attentionCount() }}</strong>
            <span>Needs action</span>
          </span>
        </div>

        <div class="task-list">
          @for (op of operations(); track op.id) {
            <article
              class="task-card"
              [attr.data-phase]="op.phase"
              [attr.data-stalled]="op.stalled ? '' : null"
            >
              <div class="status-dot" aria-hidden="true"></div>
              <div class="task-body">
                <div class="task-title-row">
                  <strong>{{ op.title }}</strong>
                  <span class="status-label">
                    @if (op.phase === 'queued' && op.queuePosition) {
                      <span class="queue-pos">#{{ op.queuePosition }}</span>
                    }
                    {{ op.statusLabel }}
                  </span>
                </div>
                <p>{{ op.message }}</p>
                <dl>
                  @if (op.txidHex) {
                    <div>
                      <dt>Tx</dt>
                      <dd>
                        <a
                          class="tx-link"
                          [href]="explorerTxUrl(op.txidHex)"
                          target="_blank"
                          rel="noopener noreferrer"
                          [title]="op.txidHex"
                          [attr.aria-label]="
                            'View transaction ' +
                            shortId(op.txidHex) +
                            ' on the Kaspa explorer'
                          "
                          >{{ shortId(op.txidHex) }}</a
                        >
                      </dd>
                    </div>
                  }
                  @if (op.actionCount !== undefined) {
                    <div>
                      <dt>Actions</dt>
                      <dd>{{ op.actionCount }}</dd>
                    </div>
                  }
                  @if (op.matchedActionCount !== undefined) {
                    <div>
                      <dt>Matched</dt>
                      <dd>{{ op.matchedActionCount }}</dd>
                    </div>
                  }
                </dl>
                @if (op.recovery; as recovery) {
                  <div class="task-recovery">
                    <p>{{ recovery.prompt }}</p>
                    @if (recoveryService.canRun(recovery)) {
                      <kc-button
                        [text]="recovery.actionLabel || 'Open Wallet'"
                        size="s"
                        variant="secondary"
                        prefixIcon="wallet-03"
                        (buttonClick)="recoveryService.run(recovery)"
                      />
                    }
                  </div>
                }
                @if (op.retryable) {
                  <div class="task-actions">
                    <kc-button
                      text="Retry"
                      size="s"
                      variant="secondary"
                      prefixIcon="refresh-ccw-04"
                      (buttonClick)="queue.retry(op.actionId ?? op.id)"
                    />
                  </div>
                }
                @if (op.phase === 'submitted') {
                  <div class="task-actions pending-actions">
                    @if (confirmingStopTrackingId() === op.id) {
                      <div class="stop-tracking-confirm">
                        <p>
                          Stop tracking this operation? The token's queue will
                          continue, but the transaction may still complete
                          on-chain — verify it on the explorer first. This does
                          not cancel the transaction.
                        </p>
                        <div class="confirm-actions">
                          <kc-button
                            class="confirm-clear-button"
                            text="Stop tracking"
                            size="s"
                            variant="secondary"
                            (buttonClick)="confirmStopTracking(op.id)"
                          />
                          <kc-button
                            class="keep-waiting-button"
                            text="Keep waiting"
                            size="s"
                            variant="primary"
                            (buttonClick)="cancelStopTracking()"
                          />
                        </div>
                      </div>
                    } @else {
                      <kc-button
                        class="stop-tracking-button"
                        text="Stop tracking"
                        size="s"
                        variant="link-secondary"
                        (buttonClick)="requestStopTracking(op.id)"
                      />
                    }
                  </div>
                }
              </div>
              @if (op.dismissible) {
                <kc-icon-button
                  class="dismiss-button"
                  icon="close"
                  size="sm"
                  variant="secondary"
                  [ariaLabel]="'Dismiss ' + op.title"
                  (buttonClick)="queue.cancel(op.actionId ?? op.id)"
                />
              }
            </article>
          }
        </div>
      </div>
    </div>

    <!-- Always reachable when the panel grows tall / drifts up and the header
         leaves the viewport — collapse must not require scrolling to the top. -->
    @if (!isCollapsed()) {
      <div class="overlay-foot">
        <kc-button
          class="minimize-button"
          text="Minimize"
          size="s"
          variant="secondary"
          prefixIcon="chevron-down"
          [isFullWidth]="true"
          (buttonClick)="toggleCollapsed()"
        />
      </div>
    }
  </section>
}
